import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChefHat, Truck, Recycle, ShieldCheck, Scale, Leaf, Award, Clock, CheckCircle2, Phone, MapPin, Users, TrendingUp, Euro, Utensils } from "lucide-react";
import { Link } from "react-router-dom";

const HuilesAlimentaires = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Collecte d'Huiles Alimentaires Usagées",
    "description": "Service de collecte d'huiles de friture usagées pour restaurants, cantines et fast-foods. Solution écologique et conforme",
    "provider": {
      "@type": "Organization",
      "name": "Groupe Fabre Environnement",
      "url": "https://groupefabre.com"
    },
    "serviceType": "Collecte et recyclage d'huiles alimentaires",
    "areaServed": {
      "@type": "Place",
      "name": "France"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <SEO 
        title="Collecte d'Huiles Alimentaires Usagées - Restaurants, Cantines, Fast-foods | Groupe Fabre" 
        description="✓ Collecte d'huiles de friture ✓ Restaurants, cantines, fast-foods ✓ Transformation en biodiesel ✓ Service conforme ✓ Contenants fournis ✓ Devis gratuit"
        canonical="/services/huiles-alimentaires"
        keywords="collecte huiles alimentaires, huiles friture, restaurant, cantines, fast-food, biodiesel, recyclage, valorisation, déchets alimentaires"
      />
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-fabre-green text-white py-20 mt-20 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-32 -translate-x-32"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white/20 text-white border-white/30 hover:bg-white/30">
              <ChefHat className="w-4 h-4 mr-2" />
              Service Restauration Professionnel
            </Badge>
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 leading-tight">
              Collecte d'Huiles 
              <span className="text-yellow-200"> Alimentaires</span> Usagées
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100 leading-relaxed">
              Solutions écologiques pour restaurants, cantines, fast-foods et établissements de restauration
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 font-semibold px-8">
                  <Phone className="w-5 h-5 mr-2" />
                  Demander un devis gratuit
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-3 border-white text-white hover:bg-white hover:text-orange-600 px-8 shadow-2xl bg-white/10 backdrop-blur-sm font-semibold">
                <Utensils className="w-5 h-5 mr-2" />
                Solutions restaurants
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-200 mb-2">100%</div>
                <p className="text-gray-200">Valorisation matière</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-200 mb-2">500+</div>
                <p className="text-gray-200">Établissements partenaires</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-200 mb-2">Biodiesel</div>
                <p className="text-gray-200">Transformation écologique</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* Services Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-orange-500">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-orange-600 text-xl">
                    <ChefHat className="mr-3 h-7 w-7" />
                    Établissements Concernés
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    <li className="flex items-start group">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Restaurants</span>
                        <p className="text-sm text-gray-600 mt-1">Traditionnels, gastronomiques, brasseries</p>
                      </div>
                    </li>
                    <li className="flex items-start group">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Fast-foods</span>
                        <p className="text-sm text-gray-600 mt-1">Chaînes et indépendants</p>
                      </div>
                    </li>
                    <li className="flex items-start group">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Cantines</span>
                        <p className="text-sm text-gray-600 mt-1">Scolaires, d'entreprises, hospitalières</p>
                      </div>
                    </li>
                    <li className="flex items-start group">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Boulangeries-pâtisseries</span>
                        <p className="text-sm text-gray-600 mt-1">Huiles de friture et graisses</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-orange-500">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-orange-600 text-xl">
                    <Scale className="mr-3 h-7 w-7" />
                    Types d'Huiles Collectées
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Huiles de friture</span>
                        <p className="text-sm text-gray-600 mt-1">Toutes huiles de cuisson usagées</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Huiles végétales</span>
                        <p className="text-sm text-gray-600 mt-1">Huiles de cuisson et de préparation</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Graisses alimentaires</span>
                        <p className="text-sm text-gray-600 mt-1">Graisses solides et semi-solides</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Mélanges</span>
                        <p className="text-sm text-gray-600 mt-1">Huiles et graisses mélangées</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Process Section */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
              <h2 className="text-3xl font-heading font-bold text-center mb-12 text-fabre-gray">
                De la collecte au biodiesel
              </h2>
              
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center group">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-200 transition-colors">
                    <Truck className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">1. Collecte</h3>
                  <p className="text-gray-600 text-sm">Enlèvement sur site avec contenants propres</p>
                </div>
                
                <div className="text-center group">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-200 transition-colors">
                    <Recycle className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">2. Filtration</h3>
                  <p className="text-gray-600 text-sm">Séparation et purification</p>
                </div>
                
                <div className="text-center group">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-200 transition-colors">
                    <Leaf className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">3. Transformation</h3>
                  <p className="text-gray-600 text-sm">Production de biodiesel</p>
                </div>
                
                <div className="text-center group">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-200 transition-colors">
                    <TrendingUp className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">4. Valorisation</h3>
                  <p className="text-gray-600 text-sm">Carburant vert et produits dérivés</p>
                </div>
              </div>
            </div>

            {/* Benefits Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-green-500">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-green-600 text-xl">
                    <Leaf className="mr-3 h-7 w-7" />
                    Transformation Écologique
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-gray-700 mb-4">Vos huiles deviennent :</p>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Badge variant="outline" className="mr-3 text-green-600 border-green-500">70%</Badge>
                        <span>Biodiesel (carburant vert)</span>
                      </div>
                      <div className="flex items-center">
                        <Badge variant="outline" className="mr-3 text-green-600 border-green-500">15%</Badge>
                        <span>Glycérine pour l'industrie</span>
                      </div>
                      <div className="flex items-center">
                        <Badge variant="outline" className="mr-3 text-green-600 border-green-500">10%</Badge>
                        <span>Savons et cosmétiques</span>
                      </div>
                      <div className="flex items-center">
                        <Badge variant="outline" className="mr-3 text-green-600 border-green-500">5%</Badge>
                        <span>Alimentation animale</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-blue-500">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-blue-600 text-xl">
                    <Euro className="mr-3 h-7 w-7" />
                    Avantages Économiques
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <ul className="space-y-3">
                      <li className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                        <span>Réduction des coûts d'élimination</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                        <span>Possible rémunération selon volumes</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                        <span>Optimisation logistique</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                        <span>Image éco-responsable valorisée</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Service Organization */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-orange-500">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-orange-600 text-xl">
                    <Truck className="mr-3 h-7 w-7" />
                    Organisation du Service
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Contenants adaptés</span>
                        <p className="text-sm text-gray-600 mt-1">Fourniture et entretien inclus</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Collecte programmée</span>
                        <p className="text-sm text-gray-600 mt-1">Planning adapté à votre activité</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Pesée et traçabilité</span>
                        <p className="text-sm text-gray-600 mt-1">Bon d'enlèvement systématique</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Hygiène garantie</span>
                        <p className="text-sm text-gray-600 mt-1">Contenants conformes aux normes alimentaires</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-green-500">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-green-600 text-xl">
                    <ShieldCheck className="mr-3 h-7 w-7" />
                    Conformité Réglementaire
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-gray-700 mb-4">Respect des obligations :</p>
                    <ul className="space-y-3">
                      <li className="flex items-center">
                        <Award className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span>Code de l'environnement</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span>Traçabilité des déchets</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span>Registre de suivi</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span>Certificats de valorisation</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-3xl font-heading font-bold mb-4">
                Un service adapté à votre activité
              </h2>
              <p className="text-xl mb-8 text-gray-100">
                Solution complète pour valoriser vos huiles alimentaires
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                  <Clock className="w-12 h-12 text-yellow-200 mx-auto mb-4" />
                  <h4 className="font-semibold text-lg mb-2">Flexibilité</h4>
                  <p className="text-gray-100">Fréquence et volumes adaptés à votre production</p>
                </div>
                <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                  <ShieldCheck className="w-12 h-12 text-yellow-200 mx-auto mb-4" />
                  <h4 className="font-semibold text-lg mb-2">Hygiène</h4>
                  <p className="text-gray-100">Contenants propres et conformes aux normes alimentaires</p>
                </div>
                <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                  <Users className="w-12 h-12 text-yellow-200 mx-auto mb-4" />
                  <h4 className="font-semibold text-lg mb-2">Proximité</h4>
                  <p className="text-gray-100">Service local avec interlocuteur dédié</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 font-semibold px-8">
                    <Phone className="w-5 h-5 mr-2" />
                    Contactez-nous
                  </Button>
                </Link>
                <Link to="/sites">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 shadow-xl bg-white/10 backdrop-blur-sm font-semibold">
                    <MapPin className="w-5 h-5 mr-2" />
                    Nos sites
                  </Button>
                </Link>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-heading font-bold text-center mb-8 text-fabre-gray">
                Questions fréquentes
              </h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-orange-500 pl-6">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">Quels établissements peuvent bénéficier du service ?</h3>
                  <p className="text-gray-600">Tous les établissements de restauration : restaurants, fast-foods, cantines, boulangeries, pâtisseries, etc.</p>
                </div>
                
                <div className="border-l-4 border-orange-500 pl-6">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">À quelle fréquence collectez-vous les huiles ?</h3>
                  <p className="text-gray-600">La fréquence est adaptée à votre production : hebdomadaire, bimensuelle, mensuelle ou à la demande.</p>
                </div>
                
                <div className="border-l-4 border-orange-500 pl-6">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">Que deviennent les huiles collectées ?</h3>
                  <p className="text-gray-600">Elles sont transformées principalement en biodiesel (70%), glycérine (15%), savons (10%) et alimentation animale (5%).</p>
                </div>

                <div className="border-l-4 border-orange-500 pl-6">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">Y a-t-il une rémunération pour les huiles collectées ?</h3>
                  <p className="text-gray-600">Selon les volumes et la qualité, une rémunération peut être proposée. Contactez-nous pour une évaluation.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default HuilesAlimentaires;